/** Axis-aligned bounding box in layout coordinates. */
export type BBox = { l: number; r: number; t: number; b: number };

/** Per-label collision resolver state (tangent slide + radial push). */
export type LayoutState = {
  slide: number;
  radialExtra: number;
  boxW: number;
  boxH: number;
};

/** Minimal slot fields needed for tangent/radial movement decomposition. */
export type LabelMovementSlot = {
  tangentX: number;
  tangentY: number;
  radialX: number;
  radialY: number;
  maxSlide: number;
};

export const MAX_LAYOUT_PASSES = 24;
export const MAX_CROSS_TABLE_PASSES = 24;
export const MAX_RADIAL_EXTRA = 36;
/** Collision resolution may pull labels inward over the seat pip (negative = toward table). */
export const MIN_RADIAL_EXTRA = -16;
/** Cross-table pass may overlap pips a bit more when separating neighbour labels. */
export const MIN_CROSS_TABLE_RADIAL_EXTRA = -20;

function clamp(value: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, value));
}

/** True when two axis-aligned label boxes intersect. */
export function labelBoxesOverlap(a: BBox, b: BBox): boolean {
  return a.l < b.r && a.r > b.l && a.t < b.b && a.b > b.t;
}

/** Decompose a table-space movement into tangent slide + radial offset. */
export function decomposeMovement(
  slot: LabelMovementSlot,
  moveX: number,
  moveY: number,
): { slide: number; radialExtra: number } {
  const tx = slot.tangentX;
  const ty = slot.tangentY;
  const rx = slot.radialX;
  const ry = slot.radialY;
  const det = tx * ry - ty * rx;
  if (Math.abs(det) > 1e-4) {
    return {
      slide: (moveX * ry - moveY * rx) / det,
      radialExtra: (ty * moveX - tx * moveY) / det,
    };
  }
  const dot = moveX * tx + moveY * ty;
  return { slide: dot, radialExtra: 0 };
}

export function applyLabelMovement(
  slot: LabelMovementSlot,
  state: LayoutState,
  moveX: number,
  moveY: number,
  limits?: { maxSlide?: number; maxRadialExtra?: number; minRadialExtra?: number },
): void {
  const { slide, radialExtra } = decomposeMovement(slot, moveX, moveY);
  const maxSlide = limits?.maxSlide ?? slot.maxSlide;
  const maxRadial = limits?.maxRadialExtra ?? MAX_RADIAL_EXTRA;
  const minRadial = limits?.minRadialExtra ?? MIN_RADIAL_EXTRA;
  state.slide = clamp(state.slide + slide, -maxSlide, maxSlide);
  state.radialExtra = clamp(state.radialExtra + radialExtra, minRadial, maxRadial);
}

/**
 * Resolve one overlapping pair by separating along the shallowest overlap axis.
 * Invokes onMoveI/onMoveJ with half the push vector in layout space.
 */
export function separateOverlappingAABBs(
  boxI: BBox,
  boxJ: BBox,
  onMoveI: (moveX: number, moveY: number) => void,
  onMoveJ: (moveX: number, moveY: number) => void,
  /** Fraction of overlap to resolve per step (default 0.51). */
  overlapFraction = 0.51,
): boolean {
  const overlapX = Math.min(boxI.r, boxJ.r) - Math.max(boxI.l, boxJ.l);
  const overlapY = Math.min(boxI.b, boxJ.b) - Math.max(boxI.t, boxJ.t);
  if (overlapX <= 0 || overlapY <= 0) return false;

  const centerIx = (boxI.l + boxI.r) / 2;
  const centerIy = (boxI.t + boxI.b) / 2;
  const centerJx = (boxJ.l + boxJ.r) / 2;
  const centerJy = (boxJ.t + boxJ.b) / 2;

  const primaryOverlap = Math.min(overlapX, overlapY);
  const push = primaryOverlap * overlapFraction + 1;
  let sepX = 0;
  let sepY = 0;
  if (overlapX <= overlapY) {
    sepX = centerJx >= centerIx ? 1 : -1;
  } else {
    sepY = centerJy >= centerIy ? 1 : -1;
  }

  const half = push / 2;
  onMoveI(-sepX * half, -sepY * half);
  onMoveJ(sepX * half, sepY * half);
  return true;
}

/** Merge layout deltas into slot label coordinates. */
export function mergeLayoutStateIntoSlot<
  T extends LabelMovementSlot & {
    labelX: number;
    labelY: number;
    labelSlide?: number;
    labelRadialExtra?: number;
  },
>(slot: T, state: LayoutState): T {
  const slide = state.slide;
  const radialExtra = state.radialExtra;
  return {
    ...slot,
    labelSlide: slide,
    labelRadialExtra: radialExtra,
    labelX: slot.labelX + slide * slot.tangentX + radialExtra * slot.radialX,
    labelY: slot.labelY + slide * slot.tangentY + radialExtra * slot.radialY,
  };
}

/** Merge additional cross-table deltas on top of an already-resolved slot. */
export function mergeCrossLayoutIntoSlot<
  T extends LabelMovementSlot & {
    labelX: number;
    labelY: number;
    labelSlide?: number;
    labelRadialExtra?: number;
  },
>(slot: T, crossSlide: number, crossRadial: number): T {
  return {
    ...slot,
    labelSlide: (slot.labelSlide ?? 0) + crossSlide,
    labelRadialExtra: (slot.labelRadialExtra ?? 0) + crossRadial,
    labelX: slot.labelX + crossSlide * slot.tangentX + crossRadial * slot.radialX,
    labelY: slot.labelY + crossSlide * slot.tangentY + crossRadial * slot.radialY,
  };
}
